import { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import classNames from './MoveStudentsPopup.module.scss';
import ChooseDestination from './ChooseDestination';
import DifferentGrade from './DifferentGrade';
import AvailableSeats from './AvailableSeats';
import Successes from './Successes';
import PartialSuccesses from './PartialSuccesses';
import Failure from './Failure';
import BulkProgress from '@/components/school-management/BulkAction/BulkProgress/BulkProgress';
import { StudentReportItem } from '@/school-management/types/response';
import AppModal from '@/components/AppModal/AppModal';
import {
  MoveClassDestination,
  BulkOperationResponseData,
  BulkOperationState,
  MoveStudentsBulkOperationItem,
} from '@/school-management/types/services/bulk';
import { bulkService } from '@/school-management/services/bulk';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

type ViewType =
  | 'choose_destination'
  | 'different_grade'
  | 'available_seats'
  | 'successes'
  | 'partial_successes'
  | 'failure'
  | 'progress';
type MoveSourceType = 'ChooseDestination' | 'DifferentGradeAll' | 'DifferentGradeSame';

interface MoveStudentsPopupProps {
  show?: boolean;
  students: StudentReportItem[];
  onClose: (moveedStudents: StudentReportItem[]) => void;
}

const MoveStudentsPopup = ({ show, students, onClose }: MoveStudentsPopupProps) => {
  const { user } = useAppSelector(authSelector);
  const [view, setView] = useState<ViewType>('choose_destination');
  const [classDestination, setClassDestination] = useState<MoveClassDestination>();
  const [moveSource, setMoveSource] = useState<MoveSourceType>();
  const [operationId, setOperationId] = useState<string>('');
  const [bulkOperation, setBulkOperation] = useState<BulkOperationResponseData>();

  useEffect(() => {
    if (show) {
      setView('choose_destination');
      setClassDestination(undefined);
      setMoveSource(undefined);
      setOperationId('');
      setBulkOperation(undefined);
    }
  }, [show]);

  const closePopup = useCallback(() => {
    let studentsToProcess = students || [];

    if (moveSource === 'DifferentGradeSame' && classDestination) {
      studentsToProcess = studentsToProcess.filter(s => s.gradeId === classDestination.gradeId);
    }

    if (view === 'successes') {
      onClose(studentsToProcess);
      return;
    }

    if (view === 'partial_successes') {
      const bulkStudentsMapper = (
        (bulkOperation?.results || []) as MoveStudentsBulkOperationItem[]
      ).reduce(
        (acc, s) => {
          if (s.studentId) {
            acc[s.studentId] = s;
          }

          return acc;
        },
        {} as {
          [studentId: string]: MoveStudentsBulkOperationItem;
        }
      );

      onClose(
        studentsToProcess.filter(s =>
          s.userId in bulkStudentsMapper ? bulkStudentsMapper[s.userId].success : true
        )
      );
      return;
    }

    onClose([]);
  }, [onClose, students, classDestination, moveSource, bulkOperation, view]);

  const checkDestination = (classDestination: MoveClassDestination, source: MoveSourceType) => {
    if (!classDestination) {
      return;
    }

    const studentsFromDifferentClass = students.filter(s => s.groupId !== classDestination.groupId);

    const studentsWithSameGrade = studentsFromDifferentClass.filter(
      s => s.gradeId === classDestination.gradeId
    );

    if (
      source === 'ChooseDestination' &&
      studentsWithSameGrade.length !== studentsFromDifferentClass.length
    ) {
      setView('different_grade');
      return;
    }

    const studentsToProcess =
      source === 'DifferentGradeSame' ? studentsWithSameGrade : studentsFromDifferentClass;

    if (studentsToProcess.length > classDestination.remainingCapacity) {
      setView('available_seats');
      return;
    }

    setMoveSource(source);

    if (studentsToProcess.length === 0) {
      setView('successes');
      return;
    }

    setView('progress');
    setOperationId('');

    const startMove = async () => {
      let operationId: string = '';

      try {
        const organizationId = user?.metadata?.organizationDetails?.organizationId || '';
        const result = await bulkService.moveStudents(
          organizationId,
          studentsToProcess.map(s => s.userId),
          classDestination.groupId
        );
        operationId = result.data?.data?.operationId || '';
      } catch (e) {}

      setOperationId(operationId);
      if (!operationId) {
        setView('failure');
        setBulkOperation(undefined);
      }
    };
    startMove();
  };

  const chooseDestinationView = useMemo(() => {
    return (
      <ChooseDestination
        students={students}
        onClassSelected={(classDestination: MoveClassDestination) => {
          setClassDestination(classDestination);
          checkDestination(classDestination, 'ChooseDestination');
        }}
        onClose={closePopup}
      />
    );
  }, [students, closePopup]);

  const differentGradeView = useMemo(() => {
    return (
      !!classDestination && (
        <DifferentGrade
          students={students}
          classDestination={classDestination}
          onMoveAll={() => {
            checkDestination(classDestination, 'DifferentGradeAll');
          }}
          onMoveSame={() => {
            checkDestination(classDestination, 'DifferentGradeSame');
          }}
          onBack={() => {
            setView('choose_destination');
          }}
        />
      )
    );
  }, [students, classDestination]);

  const availableSeatsView = useMemo(() => {
    return (
      !!classDestination && (
        <AvailableSeats
          students={students}
          classDestination={classDestination}
          moveSource={moveSource || ''}
          onBack={() => {
            setView('choose_destination');
          }}
        />
      )
    );
  }, [students, classDestination, moveSource]);

  const successesView = useMemo(() => {
    return (
      classDestination &&
      moveSource && (
        <Successes
          students={students}
          classDestination={classDestination}
          moveSource={moveSource}
          bulkOperation={bulkOperation}
          onClose={closePopup}
        />
      )
    );
  }, [students, classDestination, moveSource, bulkOperation, closePopup]);

  const partialSuccessesView = useMemo(() => {
    return (
      classDestination &&
      moveSource && (
        <PartialSuccesses
          students={students}
          classDestination={classDestination}
          moveSource={moveSource}
          bulkOperation={bulkOperation}
          onClose={closePopup}
        />
      )
    );
  }, [students, classDestination, moveSource, bulkOperation, closePopup]);

  const failureView = useMemo(() => {
    return (
      classDestination &&
      moveSource && (
        <Failure
          students={students}
          classDestination={classDestination}
          moveSource={moveSource}
          bulkOperation={bulkOperation}
          onClose={closePopup}
          onTryAgain={() => {
            checkDestination(classDestination, moveSource);
          }}
        />
      )
    );
  }, [students, classDestination, moveSource, bulkOperation, closePopup]);

  const bulkProgressView = useMemo(() => {
    return (
      <BulkProgress
        operationId={operationId}
        onFinish={(bulkOperation: BulkOperationResponseData) => {
          setBulkOperation(bulkOperation);

          switch (bulkOperation?.state) {
            case BulkOperationState.Completed:
              setView('successes');
              break;
            case BulkOperationState.PartialSuccess:
              setView('partial_successes');
              break;
            case BulkOperationState.Failed:
              setView('failure');
              break;
          }
        }}
      />
    );
  }, [operationId]);

  const content = useMemo(() => {
    switch (view) {
      case 'choose_destination':
        return chooseDestinationView;
      case 'different_grade':
        return differentGradeView;
      case 'available_seats':
        return availableSeatsView;
      case 'successes':
        return successesView;
      case 'partial_successes':
        return partialSuccessesView;
      case 'failure':
        return failureView;
      case 'progress':
        return bulkProgressView;
    }
  }, [
    view,
    chooseDestinationView,
    differentGradeView,
    availableSeatsView,
    successesView,
    partialSuccessesView,
    failureView,
    bulkProgressView,
  ]);

  return (
    <AppModal
      show={show}
      className={classNames.modal}
      modalContentClassName={classnames(classNames.modalContent, classNames.modalStudentsContent, {
        [classNames.progressModalContent]: view === 'progress',
      })}
      type='fade'
      blockBodyScroll={true}
    >
      {content}
    </AppModal>
  );
};

export default MoveStudentsPopup;
